import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { RateLimitModal } from '../components/auth/RateLimitModal';
import {
  clearRateLimitBlock,
  fetchRateLimitStatus,
  readRateLimitBlock,
  readRateLimitBlockFromSearchParams,
  RATE_LIMIT_BLOCKED_EVENT,
  RATE_LIMIT_CLEARED_EVENT,
  saveRateLimitBlock,
} from '../utils/rateLimitStorage';

const RateLimitContext = createContext({
  isBlocked: false,
  isStatusReady: false,
  block: null,
  refreshRateLimitStatus: async () => {},
});

export function useRateLimit() {
  return useContext(RateLimitContext);
}

export function RateLimitProvider({ children }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [block, setBlock] = useState(() => readRateLimitBlock());
  const [isStatusReady, setIsStatusReady] = useState(false);

  const applyBlock = useCallback((nextBlock) => {
    if (!nextBlock) {
      clearRateLimitBlock();
      setBlock(null);
      return;
    }
    saveRateLimitBlock(nextBlock);
    setBlock(nextBlock);
  }, []);

  const refreshRateLimitStatus = useCallback(async () => {
    const status = await fetchRateLimitStatus();
    if (status) {
      setBlock(status);
      return status;
    }
    setBlock(null);
    return null;
  }, []);

  useEffect(() => {
    const fromUrl = readRateLimitBlockFromSearchParams(searchParams);
    if (fromUrl) {
      applyBlock(fromUrl);
      setSearchParams({}, { replace: true });
    }
  }, [applyBlock, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        const status = await fetchRateLimitStatus();
        if (!cancelled) {
          if (status) {
            setBlock(status);
          } else if (!readRateLimitBlock()) {
            setBlock(null);
          }
        }
      } finally {
        if (!cancelled) {
          setIsStatusReady(true);
        }
      }
    };

    boot();

    const onBlocked = (event) => {
      setBlock(event.detail || readRateLimitBlock());
    };
    const onCleared = () => setBlock(null);

    window.addEventListener(RATE_LIMIT_BLOCKED_EVENT, onBlocked);
    window.addEventListener(RATE_LIMIT_CLEARED_EVENT, onCleared);

    return () => {
      cancelled = true;
      window.removeEventListener(RATE_LIMIT_BLOCKED_EVENT, onBlocked);
      window.removeEventListener(RATE_LIMIT_CLEARED_EVENT, onCleared);
    };
  }, []);

  useEffect(() => {
    if (!block?.blockedUntil) {
      return undefined;
    }

    const ms = new Date(block.blockedUntil).getTime() - Date.now();
    if (ms <= 0) {
      applyBlock(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      refreshRateLimitStatus();
    }, ms + 250);

    return () => clearTimeout(timer);
  }, [applyBlock, block, refreshRateLimitStatus]);

  const value = useMemo(
    () => ({
      isBlocked: Boolean(block),
      isStatusReady,
      block,
      refreshRateLimitStatus,
    }),
    [block, isStatusReady, refreshRateLimitStatus]
  );

  return (
    <RateLimitContext.Provider value={value}>
      {children}
      {block && location.pathname !== '/' ? (
        <RateLimitModal
          blockedUntil={block.blockedUntil}
          limit={block.limit}
          windowSeconds={block.windowSeconds}
          onExpired={() => refreshRateLimitStatus()}
        />
      ) : null}
    </RateLimitContext.Provider>
  );
}
